import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Permissions } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { useQuery } from '@tanstack/react-query'

export default function OrgBannerEditPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const [searchParams] = useSearchParams()
    const { hasAnyPermission } = useAuth()
    const { show } = useToast()
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [targetUrl, setTargetUrl] = useState('')
    const [imageFileIdPc, setImageFileIdPc] = useState('')
    const [imageFileIdMobile, setImageFileIdMobile] = useState('')
    const [imageFileIdApp, setImageFileIdApp] = useState('')
    const [enabled, setEnabled] = useState(true)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [priority, setPriority] = useState(0)
    const [uploadingPc, setUploadingPc] = useState(false)
    const [uploadingMobile, setUploadingMobile] = useState(false)
    const [uploadingApp, setUploadingApp] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const operatorOrgId = searchParams.get('orgId') || ''
    const listPath = operatorOrgId ? `/org-banners?orgId=${encodeURIComponent(operatorOrgId)}` : '/org-banners'

    const canManage = hasAnyPermission
        ? hasAnyPermission([Permissions.ORG_BANNER_MANAGE, Permissions.ORG_MANAGE])
        : false

    const { data: banner, isLoading } = useQuery({
        queryKey: ['org-banner', id],
        queryFn: async () => {
            const res = await api.get(`/banners/${id}`)
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        enabled: !!id && canManage,
    })

    useEffect(() => {
        if (banner) {
            setTitle(banner.title || '')
            setDescription(banner.description || '')
            setTargetUrl(banner.targetUrl || '')
            setImageFileIdPc(banner.imageFileIdPc || '')
            setImageFileIdMobile(banner.imageFileIdMobile || '')
            setImageFileIdApp(banner.imageFileIdApp || '')
            setEnabled(banner.enabled !== false)
            setStartDate(banner.startDate || '')
            setEndDate(banner.endDate || '')
            setPriority(banner.priority || 0)
        }
    }, [banner])

    if (!canManage) {
        return (
            <div className="p-8 text-center">
                <p className="text-red-600">권한이 없습니다.</p>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className="p-8 text-center">
                <p className="text-slate-500">로딩 중...</p>
            </div>
        )
    }

    const handleImageUpload = async (file, setFileId, setUploading, bannerVariant) => {
        if (!file) return
        setUploading(true)
        try {
            const form = new FormData()
            form.append('file', file)
            const q = bannerVariant
                ? `?bannerVariant=${encodeURIComponent(bannerVariant)}`
                : ''
            const res = await api.post(`/files/upload/banner-image${q}`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            const payload = res?.data?.data ?? res?.data ?? {}
            const fileId = payload.fileId || payload.id || payload.filename
            if (!fileId) throw new Error('no_file_id')
            setFileId(fileId)
            show('이미지 업로드 완료', 'success')
        } catch (err) {
            const message = err?.response?.data?.message || '이미지 업로드에 실패했습니다'
            show(message, 'error')
        } finally {
            setUploading(false)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!title.trim()) {
            show('제목을 입력해주세요', 'error')
            return
        }
        if (!imageFileIdPc) {
            show('PC WEB 이미지는 필수입니다', 'error')
            return
        }
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            show('시작일은 종료일보다 이전이어야 합니다', 'error')
            return
        }

        setSubmitting(true)
        try {
            const payload = {
                title: title.trim(),
                description: description.trim() || null,
                targetUrl: targetUrl.trim() || null,
                imageFileIdPc,
                imageFileIdMobile: imageFileIdMobile || null,
                imageFileIdApp: imageFileIdApp || null,
                enabled,
                startDate: startDate || null,
                endDate: endDate || null,
                priority: priority || 0,
            }

            await api.put(`/banners/org/${id}`, payload)
            show('수정되었습니다', 'success')
            navigate(listPath)
        } catch (err) {
            const message = err?.response?.data?.message || '수정에 실패했습니다'
            show(message, 'error')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">조직 배너 수정</h1>
                <button
                    type="button"
                    onClick={() => navigate(listPath)}
                    className="text-sm text-slate-600 hover:text-slate-900"
                >
                    목록으로
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 rounded-lg border border-slate-200 bg-white p-6">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        제목 <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="배너 제목을 입력하세요"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">설명</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="배너 설명을 입력하세요"
                        rows={3}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">링크 URL</label>
                    <input
                        type="url"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        PC WEB 이미지 <span className="text-red-500">*</span>
                    </label>
                    <p className="mb-2 text-xs text-slate-500">권장 사이즈: 1120×128px</p>
                    {imageFileIdPc && <p className="mb-2 text-xs text-slate-500">현재: {imageFileIdPc}</p>}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleImageUpload(file, setImageFileIdPc, setUploadingPc, 'PC')
                        }}
                        disabled={uploadingPc}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                    {uploadingPc && <p className="mt-1 text-xs text-slate-500">업로드 중...</p>}
                    {imageFileIdPc && !uploadingPc && (
                        <p className="mt-1 text-xs text-green-600">업로드 완료 (fileId: {imageFileIdPc})</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Mobile WEB 이미지 (선택)</label>
                    <p className="mb-2 text-xs text-slate-500">권장 사이즈: 375×96px 기준, 2배수 750×192px</p>
                    {imageFileIdMobile && <p className="mb-2 text-xs text-slate-500">현재: {imageFileIdMobile}</p>}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleImageUpload(file, setImageFileIdMobile, setUploadingMobile, 'MOBILE')
                        }}
                        disabled={uploadingMobile}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                    {uploadingMobile && <p className="mt-1 text-xs text-slate-500">업로드 중...</p>}
                    {imageFileIdMobile && !uploadingMobile && (
                        <p className="mt-1 text-xs text-green-600">업로드 완료 (fileId: {imageFileIdMobile})</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">APP 이미지 (선택)</label>
                    <p className="mb-2 text-xs text-slate-500">
                        APP 권장: 비율 <span className="font-medium text-slate-600">5.0:1</span>(예:{' '}
                        <span className="font-medium text-slate-600">1200×240px</span> 또는 <span className="font-medium text-slate-600">1000×200px</span>). 홈 배너 영역은 360dp 폭 기준 세로 약{' '}
                        <span className="font-medium text-slate-600">72dp</span>이며, 이미지는 높이에 맞춰 비율을 유지해 표시되고, 좌우 여백이 생길 수 있습니다.
                    </p>
                    {imageFileIdApp && <p className="mb-2 text-xs text-slate-500">현재: {imageFileIdApp}</p>}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleImageUpload(file, setImageFileIdApp, setUploadingApp, 'APP')
                        }}
                        disabled={uploadingApp}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                    {uploadingApp && <p className="mt-1 text-xs text-slate-500">업로드 중...</p>}
                    {imageFileIdApp && !uploadingApp && (
                        <p className="mt-1 text-xs text-green-600">업로드 완료 (fileId: {imageFileIdApp})</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">시작일</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">종료일</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">우선순위</label>
                    <input
                        type="number"
                        value={priority}
                        onChange={(e) => setPriority(Number(e.target.value))}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        min="0"
                    />
                    <p className="mt-1 text-xs text-slate-500">숫자가 클수록 먼저 표시됩니다</p>
                </div>

                <div>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            className="rounded border-slate-300"
                        />
                        <span className="text-sm font-semibold text-slate-700">활성화</span>
                    </label>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t">
                    <button
                        type="button"
                        onClick={() => navigate(listPath)}
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                    >
                        취소
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90 disabled:opacity-50"
                    >
                        {submitting ? '저장 중...' : '저장'}
                    </button>
                </div>
            </form>
        </div>
    )
}
